import React, { useState } from 'react';
import styled from 'styled-components';
import { Download, Edit2, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import Layout from './layout';
import { examineeGroupData } from '../data/examineeGroupMockData';
import ExamineeGroupModal from '../Modal/ExamineeGroupModal';

// Styled Components
const PageContainer = styled.div`
  background-color: #FFFFFF;
  border-radius: 0.5rem;
  border: 1px solid #E5E7EB;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const UploadSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
`;

const UploadAreaContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
`;

const UploadArea = styled.div`
  width: 100%;
  max-width: 500px;
  border: 2px dashed #D1D5DB;
  border-radius: 0.5rem;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #FAFAFA;

  &:hover {
    border-color: #00B5B8;
    background-color: #E0F7F8;
    transform: scale(1.02);
  }
`;

const UploadText = styled.p`
  color: #6B7280;
  font-size: 0.875rem;
  margin: 0;
`;

const UploadInput = styled.input`
  display: none;
`;

const DownloadExampleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: transparent;
  color: #00B5B8;
  border: none;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0.5rem 0;

  &:hover {
    color: #0891B2;
  }
`;

const FilterSection = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.5fr 1.5fr 1.5fr auto;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: end;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  display: block;
  margin-bottom: 0.5rem;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;

  &::placeholder {
    color: #9CA3AF;
  }

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const DateInputContainer = styled.div`
  position: relative;
`;

const DateInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  padding-right: 2.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #374151;

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }

  &::-webkit-calendar-picker-indicator {
    opacity: 0;
    position: absolute;
    right: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }
`;

const CalendarIcon = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9CA3AF;
  pointer-events: none;
`;


const AddGroupButton = styled.button`
  background-color: #000000;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: #333333;
  }
`;


const ResetButton = styled.button`
  background-color: #F9FAFB;
  color: #374151;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: #F3F4F6;
  }
`;

const SearchButton = styled.button`
  background-color: #000000;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: #333333;
  }
`;

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: transparent;
  color: #00B5B8;
  border: none;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0.5rem 0;
  margin-bottom: 1rem;

  &:hover {
    color: #0891B2;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const TableHeader = styled.th`
  padding: 0.75rem 0.5rem;
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  background-color: #F9FAFB;
  white-space: nowrap;
  min-width: ${props => props.minWidth || 'auto'};
`;

const TableCell = styled.td`
  padding: 0.75rem 0.5rem;
  font-size: 12px;
  color: #6B7280;
  border-bottom: 1px solid #E5E7EB;
  white-space: nowrap;
  text-align: center;
  min-width: ${props => props.minWidth || 'auto'};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6B7280;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 12px;
  padding: 0;

  &:hover {
    color: #374151;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: none;
  background-color: ${props => props.active ? '#1976D2' : 'transparent'};
  color: ${props => props.active ? 'white' : '#1976D2'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 0.875rem;
  min-width: 40px;
  text-decoration: ${props => props.active ? 'none' : 'underline'};

  &:hover:not(:disabled) {
    background-color: ${props => props.active ? '#1565C0' : '#E3F2FD'};
  }

  &:disabled {
    color: #9E9E9E;
    text-decoration: none;
    cursor: not-allowed;
  }
`;

const ExamineeGroup = () => {
  const [searchFilters, setSearchFilters] = useState({
    keyword: '',
    branch: '',
    startDate: '2025-06-10',
    endDate: '2025-06-12'
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [adminStates, setAdminStates] = useState(
    examineeGroupData.reduce((acc, item) => {
      acc[item.id] = item.isAdmin;
      return acc;
    }, {})
  );
  const [selectedFile, setSelectedFile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const totalPages = Math.ceil(examineeGroupData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = examineeGroupData.slice(startIndex, endIndex);

  const handleFilterChange = (field, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearch = () => {
    console.log('ค้นหาด้วย:', searchFilters);
  };

  const handleReset = () => {
    setSearchFilters({
      keyword: '',
      branch: '',
      startDate: '',
      endDate: ''
    });
  };

  const handleDownloadCSV = () => {
    console.log('ดาวน์โหลด CSV');
  };

  const handleDownloadExample = () => {
    console.log('ดาวน์โหลดตัวอย่าง v1');
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      console.log('อัปโหลดไฟล์:', file.name);
    }
  };

  const handleUploadClick = () => {
    document.getElementById('file-upload').click();
  };

  const handleAdminToggle = (id) => {
    setAdminStates(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  return (
    <Layout>
      <PageContainer>
        <UploadSection>
          <UploadAreaContainer>
            <UploadArea onClick={handleUploadClick}>
              <UploadInput
                id="file-upload"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileUpload}
              />
              <UploadText>
                {selectedFile ? `เลือกไฟล์: ${selectedFile.name}` : 'คลิกเพื่อเพิ่มสมาชิก v1'}
              </UploadText>
            </UploadArea>
            <DownloadExampleButton onClick={handleDownloadExample}>
              <Download size={16} />
              ดาวน์โหลดตัวอย่าง v1
            </DownloadExampleButton>
          </UploadAreaContainer>
          <AddGroupButton onClick={() => setIsModalOpen(true)}>
            เพิ่มกลุ่มผู้รับการตรวจ
          </AddGroupButton>
        </UploadSection>

        <FilterSection>
          <div>
            <Label>ค้นหาจาก ชื่อ-สกุล หรือ เลขบัตร</Label>
            <TextInput
              type="text"
              value={searchFilters.keyword}
              onChange={(e) => handleFilterChange('keyword', e.target.value)}
            />
          </div>

          <div>
            <Label>บริษัท</Label>
            <TextInput
              type="text"
              value={searchFilters.branch}
              onChange={(e) => handleFilterChange('branch', e.target.value)}
            />
          </div>

          <div>
            <Label>วันที่เริ่มต้น</Label>
            <DateInputContainer>
              <DateInput
                type="date"
                value={searchFilters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
              <CalendarIcon>
                <Calendar size={16} />
              </CalendarIcon>
            </DateInputContainer>
          </div>

          <div>
            <Label>ถึงวันที่</Label>
            <DateInputContainer>
              <DateInput
                type="date"
                value={searchFilters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
              <CalendarIcon>
                <Calendar size={16} />
              </CalendarIcon>
            </DateInputContainer>
          </div>

          <div>
            <SearchButton onClick={handleSearch}>
              ค้นหา
            </SearchButton>
          </div>
        </FilterSection>

        <DownloadButton onClick={handleDownloadCSV}>
          <Download size={16} />
          ดาวน์โหลด CSV
        </DownloadButton>

        <PaginationContainer>
          <PaginationButton
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            previous
          </PaginationButton>
          {[...Array(totalPages)].map((_, index) => (
            <PaginationButton
              key={index + 1}
              active={currentPage === index + 1}
              onClick={() => setCurrentPage(index + 1)}
            >
              {index + 1}
            </PaginationButton>
          ))}
          <PaginationButton
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            next
          </PaginationButton>
        </PaginationContainer>
      </PageContainer>


      <div style={{ overflowX: 'auto' }}>
        <Table>
          <thead>
            <tr>
              <TableHeader minWidth="60px">ลำดับ</TableHeader>
              <TableHeader minWidth="100px">วันที่สมัคร</TableHeader>
              <TableHeader minWidth="100px">ล็อกอินล่าสุด</TableHeader>
              <TableHeader minWidth="70px">แชท</TableHeader>
              <TableHeader minWidth="100px">แชทล่าสุด</TableHeader>
              <TableHeader minWidth="80px">รหัส</TableHeader>
              <TableHeader minWidth="120px">บัตรประชาชน</TableHeader>
              <TableHeader minWidth="80px">ผู้ใช้</TableHeader>
              <TableHeader minWidth="100px">ชื่อ-สกุล</TableHeader>
              <TableHeader minWidth="80px">บริษัท</TableHeader>
              <TableHeader minWidth="80px">แคมเปญ</TableHeader>
              <TableHeader minWidth="70px">admin</TableHeader>
              <TableHeader minWidth="70px">แก้ไข</TableHeader>
            </tr>
          </thead>
          <tbody>
            {currentData.map((row) => (
              <tr key={row.id}>
                <TableCell minWidth="60px">{row.id}</TableCell>
                <TableCell minWidth="100px">{row.registrationDate.split(' ')[0]}</TableCell>
                <TableCell minWidth="100px">{row.lastLogin.split(' ')[0]}</TableCell>
                <TableCell minWidth="70px">{row.chatCount}</TableCell>
                <TableCell minWidth="100px">{row.lastChat.split(' ')[0]}</TableCell>
                <TableCell minWidth="80px">{row.memberId}</TableCell>
                <TableCell minWidth="120px">{row.citizenId}</TableCell>
                <TableCell minWidth="80px">{row.username}</TableCell>
                <TableCell minWidth="100px">{row.name}</TableCell>
                <TableCell minWidth="80px">{row.company}</TableCell>
                <TableCell minWidth="80px">{row.campaign}</TableCell>
                <TableCell minWidth="70px">
                  <input
                    type="checkbox"
                    checked={adminStates[row.id]}
                    onChange={() => handleAdminToggle(row.id)}
                    style={{
                      cursor: 'pointer',
                      transform: 'scale(1.0)'
                    }}
                  />
                </TableCell>
                <TableCell minWidth="70px">
                  <EditButton>
                    <Edit2 size={12} />
                  </EditButton>
                </TableCell>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <ExamineeGroupModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

    </Layout>
  );
};


export default ExamineeGroup;